import { type Artifact, DisplayArtifactTypes, type TableArtifactData } from "@shared";

const DISPLAY_TYPES = new Set<string>(DisplayArtifactTypes);

/**
 * Is this artifact interrupt-like — does it want the user to respond?
 *
 * "Inline" is not a stored field; it's derived from the artifact's HITL intent.
 * A table is reviewable unless its `approval` is explicitly `false` (display-only).
 * Keyword tables ship with approval pre-baked, so they're reviewable without the
 * agent setting anything. Non-table display types have no reviewable semantics yet.
 *
 * v1: reviewable ≡ rendered inline in chat. Display-only artifacts (approval:false)
 * belong in the future ArtifactsTable/sidebar, so they don't render inline today.
 */
export function isReviewableArtifact(artifact: Artifact): boolean {
  if (!DISPLAY_TYPES.has(artifact.type)) return false;
  if (artifact.type === "keywords") {
    return (artifact.data as TableArtifactData | undefined)?.approval !== false;
  }
  return false;
}

/** The artifacts to render inline in the chat stream, in channel order. */
export function reviewableArtifacts(artifacts: Artifact[] | undefined): Artifact[] {
  return (artifacts ?? []).filter(isReviewableArtifact);
}

/** A message just enough to anchor an artifact by: an id + a tool result's text. */
export interface AnchorMessage {
  id?: string;
  type?: string;
  content?: unknown;
}

/**
 * Assign each reviewable artifact to the chat message that surfaced it — the
 * `create_or_edit_artifact` tool result names the artifact id in its text, so an
 * artifact anchors to the turn it was created in (rendered positionally in
 * history, not pinned at the bottom).
 *
 * Crucially, each artifact is claimed by its FIRST anchoring message only: if a
 * run errors and retries, the same tool result can appear twice — without this,
 * the artifact would render twice (the duplicate-card bug). Returns a map of
 * messageId → artifacts to render at that message.
 */
export function assignArtifactsToMessages(
  messages: AnchorMessage[],
  artifacts: Artifact[] | undefined
): Map<string, Artifact[]> {
  const reviewable = reviewableArtifacts(artifacts);
  const byMessage = new Map<string, Artifact[]>();
  const claimed = new Set<string>();
  for (const m of messages) {
    if (m.type !== "tool" || typeof m.content !== "string" || !m.id) continue;
    for (const artifact of reviewable) {
      if (claimed.has(artifact.id)) continue;
      if (m.content.includes(artifact.id)) {
        claimed.add(artifact.id);
        byMessage.set(m.id, [...(byMessage.get(m.id) ?? []), artifact]);
      }
    }
  }
  return byMessage;
}
